import { Bot, Check, ChevronRight, Copy, Download, FileText, GitMerge, Lock, Mail, Monitor, Moon, Search, ShieldCheck, Sun, UserCheck, X } from "lucide-react";
import { useMemo, useState, type CSSProperties } from "react";
import { BrandMark } from "../../components/Brand";
import { StatusIcon } from "../../components/StatusIcon";
import {
  CHECK_VIEWS,
  COMPETITORS,
  META_PAGES_CHECKED,
  META_ROWS,
  META_TOTALS,
  PAGE_SPEED,
  SAMPLE_AUDIT_AREAS,
  SAMPLE_DOMAIN,
  SAMPLE_PRIORITIES,
  SAMPLE_PROMPT,
  SAMPLE_REPORT_DATE,
  SAMPLE_SCORE,
  SECURITY_ROWS,
  SPAM_ROWS,
  SPAM_TOTALS,
  type CheckViewId,
  type Impact,
  type RowStatus,
} from "./landingData";

function ScoreRing({ score }: { score: number }) {
  return (
    <div className="score-ring" style={{ "--score": `${score * 3.6}deg` } as CSSProperties}>
      <div><strong>{score}</strong><span>/ 100</span></div>
    </div>
  );
}

export function HeroReport() {
  return (
    <div className="lp-app" aria-hidden="true">
      <aside className="lp-app-side">
        <div className="lp-app-brand"><BrandMark size={26} className="lp-mini-mark" /><span><strong>ReportGen</strong><small>by KittyLaunch</small></span></div>
        <div className="lp-app-views">
          <span data-active>Overview</span><span>SEO Audit</span><span>Technical SEO</span><span>Security &amp; Trust</span>
        </div>
        <div className="lp-app-nav">
          <small>Overview</small>
          <span data-active>Report summary</span><span>Priority issues</span><span>Top competitors</span>
        </div>
        <div className="lp-app-foot"><strong>{SAMPLE_AUDIT_AREAS} audit areas</strong><small>Updated {SAMPLE_REPORT_DATE}</small></div>
      </aside>
      <div className="lp-app-main">
        <div className="lp-app-top">
          <div><strong>{SAMPLE_DOMAIN}</strong><small>{SAMPLE_REPORT_DATE}</small></div>
          <div className="lp-app-top-actions">
            <span className="lp-app-theme"><Sun size={12} /><Moon size={12} /><Monitor size={12} /></span>
            <span className="lp-app-export"><Download size={12} />Export</span>
          </div>
        </div>
        <div className="lp-app-tabs"><span data-active>Overview</span><span>SEO Audit <small>9</small></span><span>Technical SEO <small>12</small></span><span>Security &amp; Trust <small>2</small></span></div>
        <div className="lp-app-content">
          <div className="lp-app-overview">
            <div>
              <span className="eyebrow">Website audit</span>
              <h3>{SAMPLE_DOMAIN}</h3>
              <p>An interactive website audit created on {SAMPLE_REPORT_DATE}, with clear priorities and page-level findings.</p>
              <div className="lp-app-facts"><span>{SAMPLE_AUDIT_AREAS} audit areas</span><span>{SAMPLE_PRIORITIES.length} priority issues</span><span>Expert analysis</span></div>
            </div>
            <ScoreRing score={SAMPLE_SCORE} />
          </div>
          <div className="lp-app-priorities">
            <div className="lp-app-priorities-head">
              <div><span className="eyebrow">Action plan</span><h4>Priority issues</h4></div>
              <span className="lp-app-copy"><Copy size={11} />Copy action plan</span>
            </div>
            <div className="lp-app-priority-grid">
              {SAMPLE_PRIORITIES.slice(0, 4).map((item) => (
                <div className="lp-app-priority" key={item.title}>
                  <span className="impact-pill" data-impact={item.impact}>{item.impact} impact</span>
                  <strong>{item.title}</strong>
                  <p>{item.description}</p>
                  <em>{item.scope}</em>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const IMPACT_FILTERS: Array<Impact | "all"> = ["all", "high", "medium", "low"];

export function PriorityBoard({ onOpenSample }: { onOpenSample: () => void }) {
  const [impact, setImpact] = useState<Impact | "all">("all");
  const rows = SAMPLE_PRIORITIES.filter((item) => impact === "all" || item.impact === impact);
  const shown = rows.slice(0, 6);
  return (
    <div className="lp-panel lp-priority-board">
      <div className="lp-panel-head">
        <div><span className="lp-panel-kicker">Action plan</span><strong>Priority issues</strong></div>
        <div className="impact-filter" role="group" aria-label="Filter by impact">
          {IMPACT_FILTERS.map((value) => {
            const count = value === "all" ? SAMPLE_PRIORITIES.length : SAMPLE_PRIORITIES.filter((item) => item.impact === value).length;
            return <button type="button" key={value} className={impact === value ? "is-active" : ""} aria-pressed={impact === value} onClick={() => setImpact(value)}>{value} <span>{count}</span></button>;
          })}
        </div>
      </div>
      <ol className="lp-priority-list">
        {shown.map((item, index) => (
          <li key={item.title} style={{ "--i": index } as CSSProperties}>
            <span className="lp-rank">{String(SAMPLE_PRIORITIES.indexOf(item) + 1).padStart(2, "0")}</span>
            <span className="impact-pill" data-impact={item.impact}>{item.impact}</span>
            <div><strong>{item.title}</strong><p>{item.description}</p></div>
            <em>{item.scope}</em>
            <ChevronRight size={16} aria-hidden="true" />
          </li>
        ))}
      </ol>
      <div className="lp-panel-foot">
        <span>{rows.length > shown.length ? `${rows.length - shown.length} more in the full report` : `${rows.length} of ${SAMPLE_PRIORITIES.length} priorities`}</span>
        <button type="button" className="lp-text-link" onClick={onOpenSample}>See them in the sample report <ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

export function SpamRiskPanel({ onOpenSample }: { onOpenSample: () => void }) {
  return (
    <div className="lp-panel lp-spam-panel">
      <div className="lp-panel-head">
        <div><span className="lp-panel-kicker">Spam risk</span><strong>Over-optimization risk</strong></div>
        <span className="lp-panel-meta">{SAMPLE_DOMAIN}</span>
      </div>
      <div className="lp-spam-metrics">
        <div><span><StatusIcon status="warning" />Flagged pages</span><strong>{SPAM_TOTALS.flagged}</strong></div>
        <div><span><StatusIcon status="warning" />In meta tags</span><strong>{SPAM_TOTALS.meta}</strong></div>
        <div><span><StatusIcon status="warning" />In headings</span><strong>{SPAM_TOTALS.headings}</strong></div>
        <div><span><StatusIcon status="warning" />In main content</span><strong>{SPAM_TOTALS.content}</strong></div>
      </div>
      <div className="lp-spam-table" role="table" aria-label="Over-optimization findings preview">
        <div className="lp-spam-row lp-spam-row-head" role="row">
          <span role="columnheader">Page</span><span role="columnheader">What we found</span><span role="columnheader">Where</span><span role="columnheader">Result</span>
        </div>
        {SPAM_ROWS.map((row) => (
          <div className="lp-spam-row" role="row" key={row.page} data-status={row.status}>
            <span role="cell" className="lp-mono">{row.page}</span>
            <span role="cell" className="lp-spam-evidence">{row.evidence}</span>
            <span role="cell" className="lp-spam-areas">{row.areas.map((area) => <i key={area}>{area}</i>)}</span>
            <span role="cell"><span className="lp-status-pill" data-status={row.status}>{row.label}</span></span>
          </div>
        ))}
      </div>
      <div className="lp-panel-foot">
        <span>Preview of {SPAM_ROWS.length} of {SPAM_TOTALS.flagged} flagged pages</span>
        <button type="button" className="lp-text-link" onClick={onOpenSample}>See the full analysis <ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

function CheckPreview({ view }: { view: CheckViewId }) {
  if (view === "seo") {
    return (
      <div className="lp-preview">
        <div className="lp-preview-title">Meta descriptions</div>
        <div className="lp-mini-metrics">
          {(Object.keys(META_TOTALS) as RowStatus[]).map((status) => <div key={status}><span><StatusIcon status={status} />{META_TOTALS[status].label}</span><strong>{META_TOTALS[status].count}</strong></div>)}
        </div>
        <div className="lp-mini-rows">
          {META_ROWS.filter((row) => ["/account/sign-in", "/newsletter", "/"].includes(row.page)).map((row) => <div key={row.page}><StatusIcon status={row.status} /><span className="lp-mono">{row.page}</span><em>{row.description.length} chars</em></div>)}
        </div>
      </div>
    );
  }
  if (view === "technical") {
    return (
      <div className="lp-preview">
        <div className="lp-preview-title">Page speed</div>
        <div className="lp-mini-metrics">
          <div><span><StatusIcon status="critical" />Slow pages</span><strong>{PAGE_SPEED.totals.critical}</strong></div>
          <div><span><StatusIcon status="warning" />Needs work</span><strong>{PAGE_SPEED.totals.warning}</strong></div>
          <div><span><StatusIcon status="good" />Fast pages</span><strong>{PAGE_SPEED.totals.good}</strong></div>
        </div>
        <div className="lp-mini-rows">
          {PAGE_SPEED.rows.map((row) => <div key={row.page}><StatusIcon status={row.status} /><span className="lp-mono">{row.page}</span><em>Score {row.score}</em></div>)}
        </div>
      </div>
    );
  }
  return (
    <div className="lp-preview">
      <div className="lp-preview-title">Security headers and email</div>
      <div className="lp-mini-rows lp-mini-rows-wide">
        {SECURITY_ROWS.map((row) => (
          <div key={row.name}><StatusIcon status={row.status} /><span><b>{row.name}</b><small>{row.value}</small></span><span className="lp-status-pill" data-status={row.status}>{row.label}</span></div>
        ))}
      </div>
    </div>
  );
}

export function CheckViews() {
  const [view, setView] = useState<CheckViewId>("seo");
  const active = CHECK_VIEWS.find((item) => item.id === view)!;
  return (
    <div className="lp-panel lp-checks">
      <div className="lp-checks-tabs" role="tablist" aria-label="Report areas">
        {CHECK_VIEWS.map((item) => (
          <button key={item.id} type="button" role="tab" id={`lp-tab-${item.id}`} aria-selected={view === item.id} aria-controls="lp-checks-panel" data-active={view === item.id || undefined} onClick={() => setView(item.id)}>
            {item.label}<small>{item.checks.length}</small>
          </button>
        ))}
      </div>
      <div className="lp-checks-body" role="tabpanel" id="lp-checks-panel" aria-labelledby={`lp-tab-${view}`} key={view}>
        <div className="lp-checks-list">
          <p>{active.summary}</p>
          <ul>
            {active.checks.map((check) => <li key={check.name}><Check size={14} aria-hidden="true" /><span><strong>{check.name}</strong>{check.text}</span></li>)}
          </ul>
        </div>
        <CheckPreview view={view} />
      </div>
    </div>
  );
}

export function PageDetailDemo() {
  const [status, setStatus] = useState<RowStatus | null>("warning");
  const [query, setQuery] = useState("");
  const rows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return META_ROWS.filter((row) => (!status || row.status === status) && (!needle || `${row.page} ${row.description}`.toLocaleLowerCase().includes(needle)));
  }, [status, query]);
  return (
    <div className="lp-panel lp-detail">
      <div className="lp-panel-head">
        <div><span className="lp-panel-kicker">SEO Audit</span><strong>Meta descriptions</strong></div>
        <span className="lp-panel-meta">{META_PAGES_CHECKED} pages checked</span>
      </div>
      <div className="lp-detail-metrics">
        {(Object.keys(META_TOTALS) as RowStatus[]).map((key) => (
          <button type="button" key={key} data-active={status === key || undefined} aria-pressed={status === key} onClick={() => setStatus(status === key ? null : key)}>
            <span><StatusIcon status={key} />{META_TOTALS[key].label}</span>
            <strong>{META_TOTALS[key].count}</strong>
            <small>{status === key ? "Showing these pages" : "Click to filter"}</small>
          </button>
        ))}
      </div>
      <div className="lp-detail-tools">
        <label className="search-field"><Search size={14} aria-hidden="true" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search pages" aria-label="Search pages" /></label>
        {status && <button type="button" className="lp-clear" onClick={() => setStatus(null)}><X size={12} />Clear filter</button>}
      </div>
      <div className="lp-detail-rows">
        {rows.map((row, index) => (
          <div key={row.page} data-status={row.status} style={{ "--i": index } as CSSProperties}>
            <StatusIcon status={row.status} />
            <span className="lp-detail-page">{row.page}</span>
            <span className="lp-detail-text">{row.description}</span>
            <span className="lp-detail-length">{row.description.length}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="lp-detail-empty">{status && META_TOTALS[status].count === 0 && !query.trim() ? "None found. Every checked page has a meta description." : "No pages in this preview match your search."}</div>}
      </div>
      <div className="lp-panel-foot">
        <span>Preview: {rows.length} of {status ? META_TOTALS[status].count : META_PAGES_CHECKED} pages</span>
        <span>Full list in your report</span>
      </div>
    </div>
  );
}

export function PromptDemo() {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(SAMPLE_PROMPT);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="lp-panel lp-prompt">
      <div className="lp-prompt-head">
        <div><span className="lp-panel-kicker">AI handoff</span><strong>Security remediation prompt</strong><small>2 findings from the Security headers section</small></div>
      </div>
      <div className="lp-prompt-note"><ShieldCheck size={15} aria-hidden="true" /><span>Paste it into an AI coding tool opened in your website's project.</span></div>
      <pre className="lp-prompt-body" tabIndex={0} aria-label="Sample AI fix prompt">{SAMPLE_PROMPT}</pre>
      <div className="lp-prompt-foot">
        <span>{SAMPLE_PROMPT.length.toLocaleString("en")} characters</span>
        <button type="button" className="button button-primary" onClick={copy}>{copied ? <Check size={15} /> : <Copy size={15} />}{copied ? "Copied" : "Copy prompt"}</button>
      </div>
    </div>
  );
}

export function CompetitorDemo() {
  const max = Math.max(...COMPETITORS.map((item) => item.keywords));
  return (
    <div className="lp-bars">
      {COMPETITORS.map((item) => (
        <div key={item.domain} data-you={item.you || undefined}>
          <div><span>{item.domain}{item.you && <b>You</b>}</span><strong>{item.keywords}</strong></div>
          <span className="lp-bar-track"><i style={{ "--w": `${(item.keywords / max) * 100}%` } as CSSProperties} /></span>
        </div>
      ))}
    </div>
  );
}

export function PrivateDemo() {
  return (
    <div className="lp-lock" aria-hidden="true">
      <span className="lp-lock-icon"><Lock size={16} /></span>
      <span className="lp-lock-kicker">Private report</span>
      <strong>Verify your email to continue</strong>
      <span className="lp-lock-input"><Mail size={13} />you@company.com</span>
      <span className="lp-lock-button">Send verification code</span>
      <span className="lp-lock-note"><ShieldCheck size={13} />Only the email you ordered with can open it.</span>
    </div>
  );
}

export function ExportDemo() {
  return (
    <div className="lp-export" aria-hidden="true">
      <div><Download size={16} /><span><strong>Export as PDF</strong><small>Print-ready A4 document</small></span></div>
      <div><FileText size={16} /><span><strong>Export to Markdown</strong><small>Portable text with full report data</small></span></div>
      <div><Bot size={16} /><span><strong>Copy to AI Agent</strong><small>The whole report as context for your agent</small></span></div>
    </div>
  );
}

export function HonestDemo() {
  const items = [
    { status: "good" as const, label: "Confirmed zero", text: "We checked. Nothing was found." },
    { status: "neutral" as const, label: "Not included", text: "This data was not available, so we say so." },
    { status: "warning" as const, label: "Needs your judgment", text: "Could be intentional. Here is why." },
  ];
  return (
    <div className="lp-honest">
      {items.map((item) => <div key={item.label}><StatusIcon status={item.status} /><span><strong>{item.label}</strong>{item.text}</span></div>)}
    </div>
  );
}

const SCAN_LOG = [
  { code: 200, path: "/" },
  { code: 200, path: "/discover" },
  { code: 200, path: "/launches/today" },
  { code: 404, path: "/resources/launch-scorecard" },
  { code: 200, path: "/categories/productivity" },
  { code: 410, path: "/newsletter/archive/2025-11" },
  { code: 200, path: "/pricing" },
];

const INVESTIGATION = [
  { tone: "good", icon: Check, label: "Confirmed", text: "Seven meta descriptions are too short" },
  { tone: "neutral", icon: X, label: "False alarm removed", text: "Sign-in page is blocked on purpose" },
  { tone: "accent", icon: GitMerge, label: "Root cause found", text: "Five H1 issues come from three shared templates" },
];

export function PipelineDemo() {
  return (
    <div className="lp-pipeline">
      <article>
        <header><span>01</span><strong>Scan</strong><em>Our system</em></header>
        <p>Our system crawls every public page and collects data on content, speed, links, structured data, security headers and DNS.</p>
        <div className="lp-scan-log" aria-hidden="true">
          {SCAN_LOG.map((line) => <div key={line.path} data-error={line.code >= 400 || undefined}><b>{line.code}</b><span>GET {line.path}</span></div>)}
        </div>
      </article>
      <article data-expert>
        <header><span>02</span><strong>Investigate</strong><em>Your expert</em></header>
        <p>Your expert opens your pages, confirms every issue by hand, removes false alarms and traces each problem back to its cause.</p>
        <div className="lp-reviews" aria-hidden="true">
          {INVESTIGATION.map((item) => {
            const Icon = item.icon;
            return <div key={item.text} data-tone={item.tone}><Icon size={13} /><span><b>{item.label}</b>{item.text}</span></div>;
          })}
        </div>
      </article>
      <article data-expert>
        <header><span>03</span><strong>Build your plan</strong><em>Your expert</em></header>
        <p>Your expert ranks every fix by impact, writes the next steps in plain language and prepares the AI fix prompts for each section.</p>
        <div className="lp-plan" aria-hidden="true">
          {SAMPLE_PRIORITIES.slice(0, 3).map((item, index) => (
            <div key={item.title}><b>{index + 1}</b><span className="impact-pill" data-impact={item.impact}>{item.impact}</span><span>{item.title}</span></div>
          ))}
          <footer><UserCheck size={13} />Written by your KittyLaunch expert</footer>
        </div>
      </article>
    </div>
  );
}
